import { GPSCoordinate } from '../../shared/types';
import { deriveBackendApiBaseFromLocation, WebGunService } from './web-gun-service';
import { CONFIG } from '../../shared/config';
import { isPlaceRoomId } from '../../shared/place-rooms';
import { isTechSupportId } from '../../shared/techsupport';
import {
  CONTACTS_ONLY_SCOPE_ID,
  isLocalOnlyRoomScope,
  hasNoHubRoster,
  isPersonalListScope,
  NEARBY_V3_ROOM_ID,
  type NearbyPrivacyMode,
} from '../../shared/nearby-rooms';
import { ROOM_MEMBERSHIP_TTL_SECONDS } from '../../shared/p2p-runtime';
import {
  foldSlotsIntoPrunedAggregate,
  incrementVisitSlot,
  planVisitCounterPrune,
  readPrunedVisitAggregate,
  readVisitCounterState,
  readVisitSlot,
  visitTotalsWithPruned,
  type PrunedVisitAggregate,
  type VisitCounterSlot,
} from '../../shared/visit-counter';
import type {
  ActivateRoomInput,
  ActiveExchangeRoomController,
} from '../../shared/active-exchange-room';

/**
 * Membership heartbeat cadence. Every member re-publishes its roster record this often and every
 * room-mate receives it, so relay/client traffic grows with (room size / interval) — see
 * docs/testing/room-heartbeat-load-test.md. Kept at a third of the membership TTL (180 s default),
 * so a member gets three chances before it counts as gone.
 */
export const MEMBERSHIP_HEARTBEAT_MAX_MS = 60_000;
/** Public keys ride on the first heartbeat and then every Nth one (see startMembershipHeartbeat). */
export const MEMBERSHIP_KEY_REFRESH_BEATS = 10;
/**
 * Native clients publish room membership through their embedded node's explicit hub relay. The
 * browser's Gun subscription is still the fastest/richest source (it carries pub/epub), but it
 * cannot be the only source: relay-only native membership is authoritative in the hub's REST
 * index and may never appear in a website tab's local Gun graph. Poll on the same cadence as the
 * embedded relay's room observation loop.
 */
export const SERVER_MEMBERS_POLL_MS = 5_000;

type ChatroomMember = {
  userId: string;
  stageName: string;
  /** True when this user deliberately entered a room other than their home room. */
  isTraveler?: boolean;
  joinedAt?: string | Date;
  lastSeen?: string;
  epub?: string;
  pub?: string;
  /** Random per app run; a change means the member restarted (see PeerMeshService.joinRoom). */
  epoch?: string;
};

/**
 * What a roster listener reacts to: who is here, their names and keys, and restarts (epoch).
 * Heartbeats only move `lastSeen`, so they leave this unchanged (OPEN-51: four phones re-emitted
 * an identical roster 2–3 times a second, and every emit re-ran mesh join, discovery and catch-up).
 */
export function memberRosterSignature(members: readonly ChatroomMember[]): string {
  return members
    .map((member) => [
      member.userId,
      member.stageName,
      member.isTraveler === true ? 't' : '',
      member.joinedAt instanceof Date ? member.joinedAt.toISOString() : member.joinedAt || '',
      member.epub || '',
      member.pub || '',
      member.epoch || '',
    ].join('\u0001'))
    .sort()
    .join('\u0002');
}

/**
 * Identifies this app run in the room roster. When a peer's epoch changes it restarted, and any
 * WebRTC session to it is dead even though the transport may still report "connected" for the
 * ~30–40 s it takes to notice — on hardware that stalled re-linking a restarted phone for minutes.
 */
export const MEMBERSHIP_RUN_EPOCH = Math.random().toString(36).slice(2, 10);

/**
 * Local-debug/E2E capacity (CONFIG: dev build value, test env, or `?e2e_capacity=`). Only honoured
 * outside production bundles: in production the signed protocol manifest's capacity always wins,
 * so a URL parameter can never change a Place's or a Nearby list's size.
 */
export function chatroomCapacityTestOverride(
  nodeEnv = process.env.NODE_ENV,
  configured = CONFIG.CHATROOM_MAX_CAPACITY,
  releaseDefault = 498,
): number | null {
  if (nodeEnv === 'production') return null;
  return Number.isSafeInteger(configured) && configured > 0 && configured !== releaseDefault ? configured : null;
}

export class WebChatroomService {
  private currentChatroomId?: string;
  private activeMembersUnsubscribe?: () => void;
  private memberCountSubscriptions: Map<string, () => void> = new Map(); // Track subscriptions for cleanup
  private visitCountSubscriptions: Map<string, () => void> = new Map();
  /** Live map for the active subscribeToMembers listener — reused when reopening the same room so we never flash empty. */
  private activeMembersForList = new Map<string, ChatroomMember>();
  /** Members currently backed by a fresh Gun record, independently of the REST snapshot. */
  private activeGunMemberIdsForList = new Set<string>();
  /** Members in the most recent successful authoritative server roster snapshot. */
  private serverMemberIdsForList = new Set<string>();
  private membersListDebounce: ReturnType<typeof setTimeout> | null = null;
  private membersListCallback?: (members: ChatroomMember[]) => void;
  private membersListenerRoomId: string | undefined = undefined;
  private personalRoster: ChatroomMember[] = [];
  private personalSelfUserId: string | undefined;
  /** Which personal-list scope the pushed roster belongs to (Nearby or a Place). */
  private personalScopeId: string | undefined;
  private readonly personalCountCallbacks = new Map<string, Set<(count: number) => void>>();
  private serverMembersPollTimer: ReturnType<typeof setInterval> | null = null;
  /**
   * Gun's `.map().on()` only re-fires a member's callback when THAT member's own node is
   * next written to (a new heartbeat, an explicit isActive:false on leave). A member who
   * stops heartbeating without ever sending that explicit leave signal — e.g. an app killed
   * mid-session, a test device that had its local storage cleared — never re-fires, so their
   * entry just sits in activeMembersForList forever once added, even long past the TTL: the
   * server's own in-memory roster prunes on every read (getFastActiveMembers), but this
   * client-side live view had no equivalent, which is how a stale peer accumulates into a
   * duplicate/ghost roster row a real, still-live peer only ever inserted once (confirmed via
   * real-device testing, 2026-08-09: two "chatroom-member-item" rows for the same display
   * name, only one of which the server's REST /api/chatrooms/:id/members endpoint agreed was
   * still live). Sweep the map on a timer, independent of any new Gun write, so a ghost ages
   * out client-side the same way it already does server-side.
   */
  private staleMembersSweepTimer: ReturnType<typeof setInterval> | null = null;
  private membershipHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private membershipHeartbeatKey: string | null = null;
  private membershipHeartbeatStageName: string | null = null;
  /** Set by the app to expose the CURRENT user's stage name to heartbeat beats (see beat()). */
  private membershipStageNameResolver: (() => string) | null = null;
  /** Set by the app so every join/heartbeat publishes the current room's travel status. */
  private membershipTravelerResolver: ((chatroomId: string) => boolean) | null = null;

  /** Every room change runs through this one queue, so two moves can never interleave. */
  private moveQueue: Promise<unknown> = Promise.resolve();
  /** Server leave syncs still in flight, by room. A move does not wait for its leave (OPEN-58);
   *  a later reserve or join of the same room does, so the DELETE cannot land after it. */
  private pendingLeaveSyncs = new Map<string, Promise<void>>();
  private activeExchangeRoomController: ActiveExchangeRoomController | null = null;
  private activeRoomInput: ((roomId: string) => ActivateRoomInput) | null = null;

  constructor(private gunService: WebGunService) {}

  setMembershipStageNameResolver(resolver: () => string): void {
    this.membershipStageNameResolver = resolver;
  }

  setMembershipTravelerResolver(resolver: (chatroomId: string) => boolean): void {
    this.membershipTravelerResolver = resolver;
  }

  configureActiveExchangeRoom(
    controller: ActiveExchangeRoomController,
    inputForRoom: (roomId: string) => ActivateRoomInput,
  ): void {
    this.activeExchangeRoomController = controller;
    this.activeRoomInput = inputForRoom;
  }

  private resolveApiBase(): string {
    if (typeof window === 'undefined') return 'https://127.0.0.1:8080';
    const { hostname, protocol, port } = window.location;
    return deriveBackendApiBaseFromLocation(protocol, hostname, port);
  }

  private isFreshActiveMember(memberData: any): boolean {
    if (!memberData || memberData.isActive !== true) return false;
    const rawSeen = memberData.lastSeen || memberData.joinedAt;
    if (!rawSeen) return true;
    const seenAt = Date.parse(String(rawSeen));
    if (!Number.isFinite(seenAt)) return true;
    return Date.now() - seenAt <= (ROOM_MEMBERSHIP_TTL_SECONDS + 5) * 1000;
  }

  /** A member record with a parseable lastSeen that is genuinely past the TTL window. */
  private isDefinitelyStale(memberData: any): boolean {
    const rawSeen = memberData?.lastSeen || memberData?.joinedAt;
    if (!rawSeen) return false;
    const seenAt = Date.parse(String(rawSeen));
    if (!Number.isFinite(seenAt)) return false;
    return Date.now() - seenAt > (ROOM_MEMBERSHIP_TTL_SECONDS + 5) * 1000;
  }

  /**
   * Fast authoritative member snapshot from the server's merged roster. `null` means the request
   * failed; an empty array is a successful empty-room snapshot and must remain distinguishable so
   * a transient network failure never wipes the last-known-good browser roster.
   */
  private async fetchMembersFromServer(chatroomId: string): Promise<ChatroomMember[] | null> {
    const controller = new AbortController();
    const timeoutMs = typeof process !== 'undefined' && process.env.DISABLE_HMR === 'true' ? 2_500 : 5_000;
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(
        `${this.resolveApiBase()}/api/chatrooms/${encodeURIComponent(chatroomId)}/members`,
        { cache: 'no-store', signal: controller.signal },
      );
      if (!res.ok) return null;
      const rows = (await res.json()) as unknown;
      if (!Array.isArray(rows)) return [];
      return rows.flatMap((row): ChatroomMember[] => {
        if (!row || typeof row !== 'object') return [];
        const record = row as { userId?: unknown; stageName?: unknown; isTraveler?: unknown };
        const userId = String(record.userId || '').trim();
        if (!userId) return [];
        return [{
          userId,
          stageName: String(record.stageName || userId),
          ...(typeof record.isTraveler === 'boolean' ? { isTraveler: record.isTraveler } : {}),
        }];
      });
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  /** Fast member-id snapshot used by broadcast receiver resolution. */
  async fetchMemberIdsFromServer(chatroomId: string): Promise<string[]> {
    const members = await this.fetchMembersFromServer(chatroomId);
    return members?.map((member) => member.userId) ?? [];
  }

  /** Select a manual Place or derive the automatic Nearby scope; legacy rooms migrate away. */
  async findOptimalChatroomHierarchical(
    location: GPSCoordinate,
    userId: string,
    lastChatroomId?: string,
    locationConfirmed = true,
    nearbyPrivacyMode: NearbyPrivacyMode = 'neighborhood',
  ): Promise<string> {
    console.log(`🔍 Finding optimal chatroom for user ${userId}`);
    console.log(`  Location: ${location.latitude}, ${location.longitude}`);
    console.log(`  Last chatroom: ${lastChatroomId || 'none (new user)'}`);

    if (nearbyPrivacyMode === 'contacts-only') return CONTACTS_ONLY_SCOPE_ID;
    if (nearbyPrivacyMode === 'location-off') {
      return lastChatroomId && isPlaceRoomId(lastChatroomId)
        ? lastChatroomId
        : CONTACTS_ONLY_SCOPE_ID;
    }
    // A deliberate Place visit survives restart. Legacy Global/tree/grid rooms do not: with a
    // confirmed fix they migrate directly into the current automatic Nearby cell.
    if (lastChatroomId && isPlaceRoomId(lastChatroomId)) return lastChatroomId;
    if (!locationConfirmed) return CONTACTS_ONLY_SCOPE_ID;
    // Nearby is my personal list (§3.7): one local scope, never a shared room to compute.
    return NEARBY_V3_ROOM_ID;
  }

  async joinChatroom(
    chatroomId: string,
    userId: string,
    stageName?: string,
  ): Promise<void> {
    await this.pendingLeaveSyncs.get(chatroomId);
    if (hasNoHubRoster(chatroomId)) {
      this.currentChatroomId = chatroomId;
      this.stopMembershipHeartbeat();
      // A Place still counts its visits (§4.2), although its members are now each one's own list.
      if (isPlaceRoomId(chatroomId)) await this.recordRoomVisit(chatroomId, userId).catch(() => undefined);
      return;
    }

    const userData: any = {
      joinedAt: new Date().toISOString(),
      isActive: true,
      lastSeen: new Date().toISOString(),
      userId: userId,
      stageName: stageName || userId, // Use stageName if provided, otherwise fall back to userId
      isTraveler: this.membershipTravelerResolver?.(chatroomId) === true,
    };

    this.currentChatroomId = chatroomId;

    console.log(`👥 Joining chatroom: ${chatroomId} as user: ${userId}`);
    console.log(`📝 User data:`, userData);

    // Use Gun's graph structure properly
    const gun = this.gunService.getGun();
    const alreadyActive = await new Promise<boolean>((resolve) => {
      const timeoutId = setTimeout(() => resolve(false), 700);
      gun.get('chatrooms').get(chatroomId).get('users').get(userId).once((data: any) => {
        clearTimeout(timeoutId);
        // A record whose heartbeat has lapsed is a previous stay: rejoining is a NEW join (fresh
        // joinedAt), so a returning user is a newcomer, not the room's oldest member.
        resolve(data?.isActive === true && this.isFreshActiveMember(data));
      });
    });
    if (alreadyActive) {
      await this.syncJoinWithServer(chatroomId, userId, userData.stageName, userData.isTraveler);
      await this.recordRoomVisit(chatroomId, userId);
      this.startMembershipHeartbeat(chatroomId, userId, userData.stageName);
      console.log(`✅ Already active in chatroom, rerecording visit: ${chatroomId}`);
      return;
    }

    // Write user to database and wait for completion (with retry logic).
    // Short per-attempt budget, matching WebGunService.put()'s convention for the same
    // unreliable-ack problem: a relay-only production hub gives a lone browser no real ack to
    // wait for, so 3s x 3 attempts was mostly dead time before this became non-fatal below.
    const writeUserWithRetry = async (maxRetries: number = 3): Promise<void> => {
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          await new Promise<void>((resolve, reject) => {
            const timeoutId = setTimeout(() => {
              reject(new Error('User data write timeout'));
            }, 800); // matches WebGunService.put()'s fast-timeout convention

            gun
              .get('chatrooms')
              .get(chatroomId)
              .get('users')
              .get(userId)
              .put(userData, (ack: any) => {
                clearTimeout(timeoutId);
                if (ack.err) {
                  console.error(
                    `❌ [Attempt ${attempt}/${maxRetries}] Failed to write user data:`,
                    ack.err,
                  );
                  reject(new Error(ack.err));
                } else {
                  console.log(
                    `✅ Successfully wrote user data to Gun.js for chatroom: ${chatroomId}`,
                  );
                  resolve();
                }
              });
          });

          // Success! Return
          return;
        } catch (error) {
          if (attempt === maxRetries) {
            // A bare ack timeout here is not proof the write failed — it's proof the relay
            // didn't answer in time. Production runs as a relay-only hub with no application
            // radata (RELAY_ONLY_HUB, p2p-runtime.ts): a lone browser's put NEVER gets a real
            // network ack from the relay itself, only from a genuine second peer if one is
            // meshed in. The local Gun graph already has this write regardless of whether an
            // ack ever arrives, so throwing here used to strand every solo user's very first
            // room join on "Failed to initialize the app" (same class of bug fixed for
            // WebGunService.put/putPrivate — see that commit). Continue optimistically instead.
            console.warn(`Gun chatroom-join write ack timed out after ${maxRetries} attempts, continuing optimistically:`, error);
            return;
          } else {
            // Wait before retry (exponential backoff)
            const delayMs = attempt * 150;
            console.log(`   Retrying user data write in ${delayMs}ms...`);
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }
        }
      }
    };

    await writeUserWithRetry();
    await this.syncJoinWithServer(chatroomId, userId, userData.stageName, userData.isTraveler);
    await this.recordRoomVisit(chatroomId, userId);

    // Legacy builds wrote exact GPS under this public room path. Room routing needs exact GPS only
    // in the local `userLocations` map above, so remove any legacy row instead of republishing it.
    gun.get('chatrooms').get(chatroomId).get('locations').get(userId).put(null);

    // Brief pause for Gun peer propagation.
    await new Promise((resolve) => setTimeout(resolve, 150));

    this.startMembershipHeartbeat(chatroomId, userId, userData.stageName);

    console.log(`✅ Successfully joined chatroom: ${chatroomId}`);
  }

  /**
   * Rename support: the membership heartbeat re-puts the member record every ~30s with the
   * stage name captured when the heartbeat started — without this restart, a rename's
   * one-time member write gets clobbered back to the OLD name on the next beat, so peers'
   * rosters (and everything that resolves peer names from them) stay permanently stale.
   * startMembershipHeartbeat is a no-op when the name is unchanged and restarts otherwise.
   */
  refreshMembershipStageName(chatroomId: string, userId: string, stageName: string): void {
    this.startMembershipHeartbeat(chatroomId, userId, stageName);
  }

  private startMembershipHeartbeat(chatroomId: string, userId: string, stageName: string): void {
    const key = `${chatroomId}:${userId}`;
    if (
      this.membershipHeartbeatKey === key &&
      this.membershipHeartbeatTimer &&
      this.membershipHeartbeatStageName === stageName
    ) {
      return;
    }
    this.stopMembershipHeartbeat();
    this.membershipHeartbeatKey = key;
    this.membershipHeartbeatStageName = stageName;
    let beatCount = 0;
    const beat = (forceKeys = false) => {
      // Read the LIVE stage name on every beat. A snapshot captured at heartbeat start goes
      // stale when a rename races the room join (join completes after the rename and starts
      // the heartbeat with the pre-rename name) — the beats then clobber the renamed member
      // record back to the old name every beat, and peers' rosters never see the new name.
      const liveName = this.membershipStageNameResolver?.() || stageName;
      const isTraveler = this.membershipTravelerResolver?.(chatroomId) === true;
      const now = new Date().toISOString();
      // Carry this member's public keys in the roster record: peers who need to encrypt
      // FOR us (epub — talk-body mailbox posting, pair offers) or open a signed mesh
      // session TO us (pub — WebRTC neighbor formation) then have the key material inline
      // and never depend on a presence/public-record lookup, which fails under
      // simultaneous-boot load. Both are public material.
      const pair = this.gunService.getStoredPair?.();
      const epub = pair?.epub;
      const pub = pair?.pub;
      // The keys are ~40% of a heartbeat's bytes and every room-mate receives every beat, so
      // send them on the first beat and refresh every MEMBERSHIP_KEY_REFRESH_BEATS-th beat only.
      // Gun merges partial puts into the existing node, so the keys stay on the record (and are
      // served to anyone who subscribes later by this member's own copy); the periodic refresh
      // re-asserts them in case a peer joined while no one still held the field.
      const includeKeys = forceKeys || beatCount % MEMBERSHIP_KEY_REFRESH_BEATS === 0;
      beatCount += 1;
      if (!epub || !pub) {
        // Diagnostic (2026-08-09 real-device investigation): a member whose heartbeat never
        // carries epub/pub never becomes a WebRTC/mailbox candidate for anyone (peers can't
        // encrypt for them or open a signed mesh session) — this is the exact, silent failure
        // mode being chased, so surface it loudly instead of the usual best-effort silence.
        console.warn(
          `⚠️ [heartbeat-diag] beat() for ${userId} has NO ${!epub && !pub ? 'epub/pub' : !epub ? 'epub' : 'pub'} ` +
            `— getStoredPair() returned: hasPair=${!!pair} hasPub=${!!pub} hasEpub=${!!epub}`,
        );
      }
      this.gunService
        .getGun()
        .get('chatrooms')
        .get(chatroomId)
        .get('users')
        .get(userId)
        .put({
          isActive: true,
          lastSeen: now,
          userId,
          stageName: liveName,
          isTraveler,
          epoch: MEMBERSHIP_RUN_EPOCH,
          ...(includeKeys && epub ? { epub } : {}),
          ...(includeKeys && pub ? { pub } : {}),
        });
      void this.syncMembershipHeartbeatWithServer(chatroomId, userId, liveName, now, isTraveler);
    };
    beat();
    this.membershipBeatNow = () => beat(true);
    const heartbeatMs = Math.max(
      1000,
      Math.min(MEMBERSHIP_HEARTBEAT_MAX_MS, Math.floor((ROOM_MEMBERSHIP_TTL_SECONDS * 1000) / 3)),
    );
    this.membershipHeartbeatTimer = setInterval(beat, heartbeatMs);
  }

  private async syncMembershipHeartbeatWithServer(
    chatroomId: string,
    userId: string,
    stageName: string,
    lastSeen: string,
    isTraveler: boolean,
  ): Promise<void> {
    if (this.membershipHeartbeatKey !== `${chatroomId}:${userId}`) return;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4_000);
    try {
      const response = await fetch(
        `${this.resolveApiBase()}/api/chatrooms/${encodeURIComponent(chatroomId)}/members/${encodeURIComponent(userId)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stageName, lastSeen, isTraveler }),
          signal: controller.signal,
        },
      );
      void response;
    } catch (error) {
      console.warn('syncMembershipHeartbeatWithServer failed (non-fatal):', error);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private stopMembershipHeartbeat(chatroomId?: string, userId?: string): void {
    this.membershipBeatNow = null;
    if (chatroomId && userId && this.membershipHeartbeatKey !== `${chatroomId}:${userId}`) return;
    if (this.membershipHeartbeatTimer) {
      clearInterval(this.membershipHeartbeatTimer);
      this.membershipHeartbeatTimer = null;
    }
    this.membershipHeartbeatKey = null;
    this.membershipHeartbeatStageName = null;
  }

  /** Register join on server index so GET /members is immediate (not Gun-map lag). */
  private async syncJoinWithServer(
    chatroomId: string,
    userId: string,
    stageName?: string,
    isTraveler = false,
  ): Promise<void> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4_000);
    try {
      await fetch(`${this.resolveApiBase()}/api/chatrooms/${encodeURIComponent(chatroomId)}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, stageName: stageName || userId, isTraveler }),
        signal: controller.signal,
      });
    } catch (error) {
      console.warn('syncJoinWithServer failed (non-fatal):', error);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Reserve a Place seat before leaving the current room. Capacity uncertainty fails closed: an
   * offline client can keep using its current room but cannot safely claim a new Place seat.
   */
  /** Remove the server/relay fast-index row as well as the peer's Gun record. */
  private async syncLeaveWithServer(chatroomId: string, userId: string): Promise<void> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4_000);
    try {
      await fetch(
        `${this.resolveApiBase()}/api/chatrooms/${encodeURIComponent(chatroomId)}/members/${encodeURIComponent(userId)}`,
        { method: 'DELETE', signal: controller.signal },
      );
    } catch (error) {
      // Offline-first leave still persists in the local Gun graph. A later
      // heartbeat cannot resurrect it because stopMembershipHeartbeat ran first.
      console.warn('syncLeaveWithServer failed (non-fatal):', error);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Record a room visit into the CRDT G-Counter (docs/TODO.md L1).
   *
   * Writes **only this user's slot**. Replaces three problems in one change:
   *   - the shared `visitCount` / `uniqueVisitorCount` read-modify-write, which lost
   *     increments whenever two joins interleaved;
   *   - double counting, because the server incremented the same scalars for the same visit;
   *   - the unbounded `visits/<visitEventId>` node written on every single visit.
   *
   * A slow or failed read is now harmless: it can only produce a LOWER slot value, and
   * merge keeps the higher one — where the old 700 ms default-to-0 wrote 1 over a real total.
   */
  private async recordRoomVisit(chatroomId: string, userId: string): Promise<void> {
    const gun = this.gunService.getGun();
    const now = new Date().toISOString();
    const slotRef = gun.get('chatrooms').get(chatroomId).get('visitCounter').get(userId);
    const existing = await new Promise<VisitCounterSlot | null>((resolve) => {
      const timeoutId = setTimeout(() => resolve(null), 700);
      slotRef.once((data: any) => {
        clearTimeout(timeoutId);
        resolve(readVisitSlot(userId, data));
      });
    });
    slotRef.put(incrementVisitSlot(existing, userId, now));
    // TODO §L2: fire-and-forget prune check, mirrored from the server's own recordVisit —
    // "trim occurs on device side" (Bernard, 2026-08-01): every Gun peer, browser included,
    // evaluates and prunes its own local graph. No relay-is-authoritative special case.
    void this.pruneVisitCounterIfNeeded(chatroomId).catch((err) => {
      console.warn('[WebChatroomService] visit-counter prune failed (non-fatal):', chatroomId, err);
    });
  }

  // readNumericRoomMetric was removed with the shared-scalar counters (docs/TODO.md L1).
  // Its 700 ms default-to-0 was bug #3: a slow read wrote 1 over a real total.

  /**
   * TODO §L2: once a room's live visit-counter slot count exceeds
   * `DEFAULT_VISIT_COUNTER_MAX_SLOTS`, fold the oldest slots (by `lastVisitedAt`) into the
   * room's pruned aggregate and delete them. Mirrors `ChatroomManager.pruneVisitCounterIfNeeded`
   * on the server exactly — same shared pure functions, same fold-before-delete ordering —
   * because under the P2P model there is no single authoritative pruner; every device (server
   * included) prunes its own local Gun graph independently.
   */
  private async pruneVisitCounterIfNeeded(chatroomId: string): Promise<void> {
    const gun = this.gunService.getGun();
    const counterRef = gun.get('chatrooms').get(chatroomId).get('visitCounter');
    // `.map().once()` is the established one-shot full-child-map read in this file (see
    // subscribeToRoomMembership's checkCapacityAndEvict) — a parent `.once()` on a Gun child
    // map is unreliable, so children are collected individually within a sync window.
    const slots: Record<string, unknown> = {};
    await new Promise<void>((resolve) => {
      counterRef.map().once((value: any, key: string) => {
        if (key) slots[key] = value;
      });
      setTimeout(resolve, 600);
    });

    const plan = planVisitCounterPrune(readVisitCounterState(slots));
    if (plan.slotsToPrune.length === 0) return;

    const prunedRef = gun.get('chatrooms').get(chatroomId).get('visitCounterPruned');
    const existingPruned = await new Promise<PrunedVisitAggregate | null>((resolve) => {
      const timeoutId = setTimeout(() => resolve(null), 700);
      prunedRef.once((data: any) => {
        clearTimeout(timeoutId);
        resolve(data ?? null);
      });
    });
    const updatedAggregate = foldSlotsIntoPrunedAggregate(
      readPrunedVisitAggregate(existingPruned),
      plan.slotsToPrune,
    );
    prunedRef.put(updatedAggregate);

    for (const slot of plan.slotsToPrune) {
      counterRef.get(slot.userId).put(null);
    }
  }

  /** `awaitServer: false` (room moves) returns once the local Gun leave is written; the server
   *  DELETE finishes in the background. */
  async leaveChatroom(chatroomId: string, userId: string, opts: { awaitServer?: boolean } = {}): Promise<void> {
    console.log(`🚪 Leaving chatroom: ${chatroomId} as user: ${userId}`);
    this.stopMembershipHeartbeat(chatroomId, userId);
    if (hasNoHubRoster(chatroomId)) {
      if (this.currentChatroomId === chatroomId) delete this.currentChatroomId;
      return;
    }

    const gun = this.gunService.getGun();
    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      const timeoutId = setTimeout(finish, 1500);
      gun
        .get('chatrooms')
        .get(chatroomId)
        .get('users')
        .get(userId)
        .put(
          {
            leftAt: new Date().toISOString(),
            isActive: false,
          },
          () => {
            clearTimeout(timeoutId);
            finish();
          },
        );
    });
    // Best-effort privacy migration for rows written by older releases.
    gun.get('chatrooms').get(chatroomId).get('locations').get(userId).put(null);
    const sync: Promise<void> = this.syncLeaveWithServer(chatroomId, userId).finally(() => {
      if (this.pendingLeaveSyncs.get(chatroomId) === sync) this.pendingLeaveSyncs.delete(chatroomId);
    });
    this.pendingLeaveSyncs.set(chatroomId, sync);
    if (opts.awaitServer !== false) await sync;
    console.log(`✅ Initiated leave for chatroom: ${chatroomId}`);
  }

  private runMove<T>(operation: () => Promise<T>): Promise<T> {
    const run = this.moveQueue.then(operation, operation);
    this.moveQueue = run.catch(() => undefined);
    return run;
  }

  /**
   * Manual room switch. Atomic with respect to every other move: it waits for any move already
   * running. The old room stops before the new one starts. A Place is never full (§3.7 "Places in
   * v3"), so there is no seat to reserve first.
   */
  async switchChatroom(userId: string, newChatroomId: string, stageName?: string): Promise<void> {
    await this.runMove(async () => {
      const from = this.currentChatroomId;
      // A same-room switch is a no-op, not a revisit: joinChatroom()'s alreadyActive
      // fast path re-records a visit unconditionally (needed for the page-reload case,
      // where a fresh service instance has no currentChatroomId to compare against).
      if (from === newChatroomId) return;
      const moveMembership = async () => {
        if (from) await this.leaveChatroom(from, userId, { awaitServer: false });
        this.currentChatroomId = newChatroomId;
        await this.joinChatroom(newChatroomId, userId, stageName);
      };
      if (newChatroomId === CONTACTS_ONLY_SCOPE_ID && this.activeExchangeRoomController) {
        await this.activeExchangeRoomController.leave();
        await moveMembership();
      } else if (this.activeExchangeRoomController && this.activeRoomInput) {
        await this.activeExchangeRoomController.transitionTo(
          this.activeRoomInput(newChatroomId),
          moveMembership,
        );
      } else {
        await moveMembership();
      }
    });
  }

  /**
   * Snapshot active user ids for a room. Uses a short `map().on` observation window so cold pages
   * do not depend on a parent `.once()` snapshot, which is unreliable for Gun child user nodes.
   */
  private async observeActiveMemberIds(
    chatroomId: string,
    observeMs = 400,
    _options: { includeTechSupport?: boolean } = {},
  ): Promise<string[]> {
    if (isPersonalListScope(chatroomId)) {
      return chatroomId === this.personalScopeId ? [...new Set(this.personalRoster.map((member) => member.userId))] : [];
    }
    if (isLocalOnlyRoomScope(chatroomId)) return [];
    try {
      const gun = this.gunService.getGun();
      const activeYes = new Set<string>();

      return await new Promise((resolve) => {
        let settled = false;
        const finish = (ids: string[]) => {
          if (settled) return;
          settled = true;
          try {
            off.off();
          } catch {
            /* Gun peer */
          }
          resolve(ids);
        };

        const off = gun
          .get('chatrooms')
          .get(chatroomId)
          .get('users')
          .map()
          .on((memberData: any, userId: string) => {
            if (userId.startsWith('_') || isTechSupportId(userId)) return;
            if (this.isFreshActiveMember(memberData)) {
              activeYes.add(userId);
            } else {
              activeYes.delete(userId);
            }
          });

        setTimeout(() => finish([...activeYes]), observeMs);
      });
    } catch (error) {
      console.error('Error getting active members:', error);
      return [];
    }
  }

  /**
   * OPEN-38: members whose record is still marked active but whose heartbeat lapsed less than
   * `maxLapseMs` ago — typically a phone whose page Android froze while asleep. They never left the
   * room (an explicit leave clears `isActive`), so a new Talk is still queued for them and a wake
   * ping lets their device notice it.
   */
  async observeLapsedMemberIds(chatroomId: string, maxLapseMs: number, observeMs = 400): Promise<string[]> {
    if (hasNoHubRoster(chatroomId)) return [];
    try {
      const gun = this.gunService.getGun();
      const lapsed = new Set<string>();
      return await new Promise((resolve) => {
        let settled = false;
        const off = gun
          .get('chatrooms')
          .get(chatroomId)
          .get('users')
          .map()
          .on((memberData: any, userId: string) => {
            if (userId.startsWith('_') || isTechSupportId(userId)) return;
            const seenAt = Date.parse(String(memberData?.lastSeen || memberData?.joinedAt || ''));
            const isLapsed = memberData?.isActive === true && this.isDefinitelyStale(memberData)
              && Number.isFinite(seenAt) && Date.now() - seenAt <= maxLapseMs;
            if (isLapsed) lapsed.add(userId);
            else lapsed.delete(userId);
          });
        setTimeout(() => {
          if (settled) return;
          settled = true;
          try { off.off(); } catch { /* Gun peer */ }
          resolve([...lapsed]);
        }, observeMs);
      });
    } catch {
      return [];
    }
  }

  /**
   * Snapshot active user ids for a room. Uses a short `map().on` observation window so we do not
   * resolve on the first partial replica (e.g. only the sender) before peers replicate — the old
   * `.once` + "resolve as soon as length > 0" path often dropped other members and yielded 0
   * broadcast receivers while the UI already listed them.
   */
  async getActiveMembers(chatroomId: string): Promise<string[]> {
    const fromServer = await this.fetchMemberIdsFromServer(chatroomId);
    if (fromServer.length > 0) return fromServer;
    return this.observeActiveMemberIds(chatroomId, 800);
  }

  /**
   * Ask peers for the room roster now. Gun does not replay existing data to a peer that connects
   * after a subscription was made, so a phone joining a nearby link (OPEN-36) saw only itself until
   * the others' next membership heartbeat (60 s). A fresh read goes out to every connected peer; the
   * answers land in the graph and the live members subscription picks them up.
   */
  /**
   * Re-announce this member now, keys included. A phone that just linked to a nearby peer (OPEN-36)
   * may have sent its key-carrying first beat before the link existed; without this the peer saw
   * the member but no pub until the 10th beat (10 min), so no mesh session could form.
   */
  announceMembershipNow(): void {
    this.membershipBeatNow?.();
  }

  private membershipBeatNow: (() => void) | null = null;

  pullMembersFromPeers(chatroomId: string): void {
    if (!chatroomId) return;
    try {
      this.gunService.getGun().get('chatrooms').get(chatroomId).get('users').map().once(() => undefined);
    } catch {
      // Best effort — the heartbeat still converges.
    }
  }

  /**
   * Nearby v3: my personal list is the Nearby audience. The app pushes it here whenever it changes,
   * and it reaches the same member callback a shared room's roster would.
   */
  pushPersonalRoster(members: ChatroomMember[], selfUserId?: string, scopeId: string = NEARBY_V3_ROOM_ID): void {
    this.personalRoster = members.slice();
    this.personalSelfUserId = selfUserId;
    this.personalScopeId = scopeId;
    for (const callback of this.personalCountCallbacks.get(scopeId) ?? []) callback(this.personalHeadcount(scopeId));
    if (this.membersListenerRoomId === scopeId && this.membersListCallback) {
      this.membersListCallback(this.personalRoster.slice());
    }
  }

  /**
   * Nearby: my list without me (§3.7 "N near you"). A Place: everyone in it, me included. Zero for a
   * scope I am not in (another Place's count comes from the hub's room list).
   */
  private personalHeadcount(scopeId: string): number {
    if (scopeId !== this.personalScopeId) return 0;
    const ids = new Set(this.personalRoster.map((member) => member.userId));
    if (!isPlaceRoomId(scopeId) && this.personalSelfUserId) ids.delete(this.personalSelfUserId);
    return ids.size;
  }

  subscribeToMembers(
    chatroomId: string,
    callback: (members: ChatroomMember[]) => void,
  ): void {
    this.membersListCallback = callback;
    if (hasNoHubRoster(chatroomId)) {
      this.activeMembersUnsubscribe?.();
      delete this.activeMembersUnsubscribe;
      this.membersListenerRoomId = chatroomId;
      const roster = chatroomId === this.personalScopeId ? [...this.personalRoster] : [];
      queueMicrotask(() => {
        if (this.membersListCallback === callback && this.membersListenerRoomId === chatroomId) callback(roster);
      });
      return;
    }

    // Reopening the same chatroom: keep Gun .map() state and only swap the callback + push a snapshot.
    // Replacing the subscription used to clear the map; the first debounced tick was often [] and wiped
    // broadcast receiver lists (server IN registration) while the UI still showed other users.
    if (this.membersListenerRoomId === chatroomId && this.activeMembersUnsubscribe) {
      queueMicrotask(() => {
        if (this.membersListCallback === callback && chatroomId === this.membersListenerRoomId) {
          callback(Array.from(this.activeMembersForList.values()));
        }
      });
      return;
    }

    if (this.activeMembersUnsubscribe) {
      this.activeMembersUnsubscribe();
    }
    this.membersListenerRoomId = chatroomId;
    this.activeMembersForList.clear();
    this.activeGunMemberIdsForList.clear();
    this.serverMemberIdsForList.clear();
    if (this.membersListDebounce) {
      clearTimeout(this.membersListDebounce);
      this.membersListDebounce = null;
    }
    if (this.serverMembersPollTimer) {
      clearInterval(this.serverMembersPollTimer);
      this.serverMembersPollTimer = null;
    }

    let emittedSignature: string | null = null;
    let emittedTo: ((members: ChatroomMember[]) => void) | null = null;
    const emitMembers = () => {
      if (chatroomId !== this.membersListenerRoomId || !this.membersListCallback) return;
      const members = Array.from(this.activeMembersForList.values());
      const signature = memberRosterSignature(members);
      if (signature === emittedSignature && emittedTo === this.membersListCallback) return;
      emittedSignature = signature;
      emittedTo = this.membersListCallback;
      this.membersListCallback(members);
    };

    // The hub REST roster is the cross-runtime convergence point. In particular, embedded phones
    // mirror their membership there even when their local Gun graph never replicates into this
    // website tab. Merge it with (rather than replace) Gun so keys and other richer fields remain.
    const refreshMembersFromServer = async () => {
      const members = await this.fetchMembersFromServer(chatroomId);
      if (members === null || chatroomId !== this.membersListenerRoomId) return;

      const visibleMembers = members.filter((member) => !isTechSupportId(member.userId));
      const nextServerIds = new Set(visibleMembers.map((member) => member.userId));
      for (const previousId of this.serverMemberIdsForList) {
        if (!nextServerIds.has(previousId) && !this.activeGunMemberIdsForList.has(previousId)) {
          this.activeMembersForList.delete(previousId);
        }
      }
      this.serverMemberIdsForList = nextServerIds;
      for (const member of visibleMembers) {
        const existing = this.activeMembersForList.get(member.userId);
        this.activeMembersForList.set(member.userId, { ...existing, ...member });
      }
      emitMembers();
    };

    const gun = this.gunService.getGun();

    const off = gun
      .get('chatrooms')
      .get(chatroomId)
      .get('users')
      .map()
      .on((memberData: any, userId: string) => {
        if (userId.startsWith('_') || isTechSupportId(userId)) return;
        // Gun's .off() (called by activeMembersUnsubscribe when switching rooms, below) stops
        // *future* subscriptions but cannot cancel an event already in flight — a message for
        // this closure's room that was queued before the switch can still be delivered after
        // `chatroomId` has stopped being the active room. Without this guard, that stale event
        // still mutated the single shared activeMembersForList map (only the *debounced
        // notification* below was gated on membersListenerRoomId, not the map write itself),
        // silently injecting a member from the room just left into the room just entered — e.g.
        // a peer from Global surviving into a just-opened hierarchy room's roster and being
        // counted as a receiver there (confirmed via e2e: 00h-chatroom-hierarchy-broadcast,
        // 2026-08-10). A room switch already clears the map and re-subscribes fresh, so any event
        // for a room that is no longer current is definitionally stale.
        if (chatroomId !== this.membersListenerRoomId) return;

        if (this.isFreshActiveMember(memberData)) {
          this.activeGunMemberIdsForList.add(userId);
          const existing = this.activeMembersForList.get(userId);
          this.activeMembersForList.set(userId, {
            ...existing,
            userId,
            // A successful server snapshot is authoritative for the current display name. Gun's
            // cached record may be older, but still contributes keys and timestamps.
            stageName: this.serverMemberIdsForList.has(userId)
              ? existing?.stageName || memberData.stageName || userId
              : memberData.stageName || userId,
            ...(memberData.lastSeen ? { lastSeen: memberData.lastSeen } : {}),
            ...(memberData.joinedAt ? { joinedAt: memberData.joinedAt } : {}),
            ...(typeof memberData.epub === 'string' && memberData.epub ? { epub: memberData.epub } : {}),
            ...(typeof memberData.pub === 'string' && memberData.pub ? { pub: memberData.pub } : {}),
            ...(typeof memberData.epoch === 'string' && memberData.epoch ? { epoch: memberData.epoch } : {}),
            ...(typeof memberData.isTraveler === 'boolean' ? { isTraveler: memberData.isTraveler } : {}),
          });
        } else if (memberData && (memberData.isActive === false || this.isDefinitelyStale(memberData))) {
          // Only remove on a definite signal (explicitly inactive, or a genuinely old lastSeen).
          // Gun's .map().on() transiently emits null/partial records mid-sync; deleting on those
          // made the roster flicker ("comes and goes"). Keep the last-known-good entry instead.
          this.activeGunMemberIdsForList.delete(userId);
          if (!this.serverMemberIdsForList.has(userId)) this.activeMembersForList.delete(userId);
        }

        if (this.membersListDebounce) clearTimeout(this.membersListDebounce);
        this.membersListDebounce = setTimeout(() => {
          if (chatroomId === this.membersListenerRoomId && this.membersListCallback) {
            emitMembers();
          }
        }, 150);
      });

    void refreshMembersFromServer();
    this.serverMembersPollTimer = setInterval(() => void refreshMembersFromServer(), SERVER_MEMBERS_POLL_MS);

    // See staleMembersSweepTimer's own doc comment: without this, a member who stops
    // heartbeating without an explicit leave signal never re-triggers Gun's .map().on()
    // callback and so is never re-evaluated for staleness, sitting in the roster forever.
    if (this.staleMembersSweepTimer) clearInterval(this.staleMembersSweepTimer);
    const sweepMs = Math.max(1000, Math.min(30_000, Math.floor((ROOM_MEMBERSHIP_TTL_SECONDS * 1000) / 3)));
    this.staleMembersSweepTimer = setInterval(() => {
      if (chatroomId !== this.membersListenerRoomId) return;
      let changed = false;
      for (const [id, member] of this.activeMembersForList.entries()) {
        if (this.isDefinitelyStale({ lastSeen: member.lastSeen, joinedAt: member.joinedAt })) {
          this.activeGunMemberIdsForList.delete(id);
          if (!this.serverMemberIdsForList.has(id)) {
            this.activeMembersForList.delete(id);
            changed = true;
          }
        }
      }
      if (changed && this.membersListCallback) {
        emitMembers();
      }
    }, sweepMs);

    this.activeMembersUnsubscribe = () => {
      off.off();
      this.membersListenerRoomId = undefined;
      this.activeMembersForList.clear();
      this.activeGunMemberIdsForList.clear();
      this.serverMemberIdsForList.clear();
      if (this.membersListDebounce) {
        clearTimeout(this.membersListDebounce);
        this.membersListDebounce = null;
      }
      if (this.serverMembersPollTimer) {
        clearInterval(this.serverMembersPollTimer);
        this.serverMembersPollTimer = null;
      }
      if (this.staleMembersSweepTimer) {
        clearInterval(this.staleMembersSweepTimer);
        this.staleMembersSweepTimer = null;
      }
    };
  }

  getCurrentChatroomId(): string | undefined {
    return this.currentChatroomId;
  }

  /**
   * Get the member count for a specific chatroom (without subscribing)
   * This is useful for displaying member counts in chatroom lists
   */
  async getMemberCount(chatroomId: string): Promise<number> {
    if (isPersonalListScope(chatroomId)) return this.personalHeadcount(chatroomId);
    if (isLocalOnlyRoomScope(chatroomId)) return 0;
    const activeMemberIds = await this.observeActiveMemberIds(chatroomId, 1400, { includeTechSupport: true });
    return activeMemberIds.length;
  }

  /**
   * Subscribe to member count updates for a specific chatroom
   * Calls the callback whenever the member count changes
   */
  subscribeToMemberCount(chatroomId: string, callback: (count: number) => void): void {
    if (isPersonalListScope(chatroomId)) {
      // One live subscriber per scope and screen, like the shared-room path below.
      this.personalCountCallbacks.set(chatroomId, new Set([callback]));
      callback(this.personalHeadcount(chatroomId));
      return;
    }
    if (isLocalOnlyRoomScope(chatroomId)) {
      callback(0);
      return;
    }
    // Unsubscribe from previous subscription for this chatroom if exists
    const existingUnsubscribe = this.memberCountSubscriptions.get(chatroomId);
    if (existingUnsubscribe) {
      existingUnsubscribe();
    }

    console.log(`👂 Subscribing to member count for chatroom: ${chatroomId}`);
    const gun = this.gunService.getGun();

    // The server publishes this aggregate alongside its presence updates.  Reading it
    // avoids a separate HTTP request and does not disclose the room's member list.
    const publicCountRef = gun
      .get('public')
      .get('room-member-counts')
      .get(chatroomId);
    const publicCountOff = publicCountRef.on((value: unknown) => {
      const count = Number((value as { count?: unknown } | null)?.count);
      if (Number.isFinite(count) && count >= 0) callback(count);
    });

    // Track active members for this chatroom
    const activeMembers = new Map<string, any>();
    let updateTimeout: NodeJS.Timeout | null = null;
    let subscriptionCancelled = false;
    let seedHydrated = false;
    let pendingLiveEmit = false;

    const emitCount = () => {
      let count = 0;
      for (const [id, data] of activeMembers) {
        if (isTechSupportId(id)) continue;
        if (this.isFreshActiveMember(data)) {
          count++;
        }
      }

      console.log(`📊 Member count update for ${chatroomId}: ${count} members`);
      callback(count);
    };

    // Seed the count through the same child-node observation path used by detail/broadcast logic.
    // This prevents the first Chatrooms screen from rendering every room as 0 until a detail view
    // happens to force that room's member list to load.
    void this.observeActiveMemberIds(chatroomId, 1400, { includeTechSupport: true }).then((memberIds) => {
      if (subscriptionCancelled) return;
      for (const userId of memberIds) {
        if (!activeMembers.has(userId)) {
          activeMembers.set(userId, { isActive: true });
        }
      }
      seedHydrated = true;
      emitCount();
      if (pendingLiveEmit) {
        pendingLiveEmit = false;
        emitCount();
      }
    });

    // Subscribe to each individual user's data using map().on()
    const off = gun
      .get('chatrooms')
      .get(chatroomId)
      .get('users')
      .map()
      .on((memberData: any, userId: string) => {
        // Skip Gun.js metadata
        if (userId.startsWith('_') || isTechSupportId(userId)) return;

        // Update our tracking map
        if (!this.isFreshActiveMember(memberData)) {
          activeMembers.delete(userId);
        } else {
          activeMembers.set(userId, memberData);
        }

        // Debounce the count update to avoid excessive recalculations
        if (updateTimeout) clearTimeout(updateTimeout);
        updateTimeout = setTimeout(() => {
          if (!seedHydrated) {
            pendingLiveEmit = true;
            return;
          }
          emitCount();
        }, 100); // 100ms debounce
      });

    // A member who goes offline just stops heartbeating: Gun never fires for that, so re-check
    // freshness on a timer and drop them from the count once their heartbeat has lapsed.
    const staleSweep = setInterval(() => {
      let removed = false;
      for (const [id, data] of activeMembers) {
        if (!this.isFreshActiveMember(data)) {
          activeMembers.delete(id);
          removed = true;
        }
      }
      if (removed && seedHydrated) emitCount();
    }, 30_000);

    // Store unsubscribe function
    this.memberCountSubscriptions.set(chatroomId, () => {
      subscriptionCancelled = true;
      clearInterval(staleSweep);
      if (updateTimeout) {
        clearTimeout(updateTimeout);
        updateTimeout = null;
      }
      off.off();
      publicCountOff?.off?.();
    });
  }

  subscribeToVisitCounts(
    chatroomId: string,
    callback: (counts: { visitCount: number; uniqueVisitorCount: number }) => void,
  ): void {
    const existingUnsubscribe = this.visitCountSubscriptions.get(chatroomId);
    if (existingUnsubscribe) existingUnsubscribe();
    const gun = this.gunService.getGun();
    // Both badges now come from one CRDT map (docs/TODO.md L1): total = sum of slots,
    // unique = number of non-zero slots. `.map()` is required — a parent `.once()` on a
    // Gun child map usually comes back empty. TODO §L2: also fold in the pruned aggregate,
    // so a device-side prune never makes this badge appear to shrink.
    const slots: Record<string, unknown> = {};
    let pruned: unknown = null;
    const emit = () => callback(visitTotalsWithPruned(readVisitCounterState(slots), readPrunedVisitAggregate(pruned)));
    const offSlots = gun
      .get('chatrooms')
      .get(chatroomId)
      .get('visitCounter')
      .map()
      .on((value: any, key: string) => {
        if (!key) return;
        slots[key] = value;
        emit();
      });
    const offPruned = gun
      .get('chatrooms')
      .get(chatroomId)
      .get('visitCounterPruned')
      .on((value: any) => {
        pruned = value;
        emit();
      });
    this.visitCountSubscriptions.set(chatroomId, () => {
      offSlots?.off?.();
      offPruned?.off?.();
    });
  }

  /**
   * Unsubscribe from all member count subscriptions
   */
  unsubscribeAllMemberCounts(): void {
    this.memberCountSubscriptions.forEach((unsubscribe) => unsubscribe());
    this.memberCountSubscriptions.clear();
    this.visitCountSubscriptions.forEach((unsubscribe) => unsubscribe());
    this.visitCountSubscriptions.clear();
  }
}
